/**
 * Shared renderer for /sworn/* dynamic routes.
 */
import { Link, useRouterState } from "@tanstack/react-router";
import { ContentFreshness } from "@/components/ContentFreshness";
import type { SwornPageContent } from "@/lib/sworn/generator";
import type { SwornEmbassy, SwornDocType } from "@/data/sworn";
import { RelatedClusters } from "@/components/RelatedClusters";
import { OfficialServiceSources } from "@/components/OfficialServiceSources";

export function SwornPageBody(props: {
  content: SwornPageContent;
  embassy?: SwornEmbassy;
  doc?: SwornDocType;
  breadcrumb?: Array<{ label: string; to?: string }>;
}) {
  const { content, embassy, doc, breadcrumb } = props;
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  return (
    <article className="mx-auto max-w-4xl px-4 py-10 prose prose-slate max-w-none">
      {breadcrumb && (
        <nav className="text-sm text-slate-500 mb-4" aria-label="Breadcrumb">
          {breadcrumb.map((b, i) => (
            <span key={i}>
              {i > 0 && " › "}
              {b.to ? <Link to={b.to}>{b.label}</Link> : <span>{b.label}</span>}
            </span>
          ))}
        </nav>
      )}
      <h1 className="text-3xl md:text-4xl font-bold text-slate-900">{content.h1}</h1>
      <p
        className="speakable-summary lead answer-summary text-lg text-slate-700"
        dangerouslySetInnerHTML={{ __html: content.intro }}
      />

      <section aria-labelledby="benefits">
        <h2 id="benefits">จุดเด่นบริการนักแปลสาบานตนของเรา</h2>
        <dl className="grid gap-4 md:grid-cols-2">
          {content.benefits.map(([k, v], i) => (
            <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <dt className="font-semibold text-slate-900">{k}</dt>
              <dd className="text-slate-700 mt-1">{v}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section aria-labelledby="steps">
        <h2 id="steps">ขั้นตอนแปลและรับรองสถานทูตทีละสเต็ป</h2>
        <ol className="space-y-3">
          {content.steps.map((s, i) => (
            <li key={i} className="rounded border-l-4 border-blue-600 bg-blue-50 p-3">
              <strong className="text-blue-900">
                {i + 1}. {s.tag} —
              </strong>{" "}
              <span className="text-slate-800">{s.text}</span>
            </li>
          ))}
        </ol>
      </section>

      {content.sections.map((sec, i) => (
        <section key={i} aria-label={sec.heading}>
          <h2>{sec.heading}</h2>
          <div dangerouslySetInnerHTML={{ __html: sec.html }} />
        </section>
      ))}

      {embassy && (
        <section aria-labelledby="embassy">
          <h2 id="embassy">รายละเอียด {embassy.nameTh}</h2>
          <ul>
            <li>
              ภาษาปลายทาง: {embassy.language} ({embassy.langCode})
            </li>
            <li>ประเภทวีซ่าที่รับ: {embassy.visaTypes.join(" · ")}</li>
            <li>เอกสารหลัก: {embassy.keyDocs.join(" · ")}</li>
          </ul>
        </section>
      )}

      {doc && (
        <section aria-labelledby="doc">
          <h2 id="doc">
            รายละเอียดเอกสาร {doc.nameTh} ({doc.nameEn})
          </h2>
          <ul>
            <li>หมวด: {doc.category}</li>
            <li>ค่าบริการ: สอบถามทางโทรศัพท์ · LINE · Email</li>
            <li>
              ระยะเวลาดำเนินการ: ขึ้นกับภาษา เอกสาร และลำดับการรับรอง
              โปรดยืนยันกับเจ้าหน้าที่ก่อนเริ่มงาน
            </li>
          </ul>
        </section>
      )}

      <section
        aria-labelledby="faq"
        style={{ contentVisibility: "auto", containIntrinsicSize: "600px 800px" }}
      >
        <h2 id="faq">คำถามที่พบบ่อย</h2>
        <div className="space-y-4">
          {content.faqs.map((f, i) => (
            <details key={i} className="rounded border border-slate-200 p-4">
              <summary className="font-semibold cursor-pointer text-slate-900">{f.q}</summary>
              <p className="mt-2 text-slate-700 faq-answer">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="mt-10 rounded-lg bg-blue-600 p-6 text-white">
        <h2 className="text-white text-2xl">พร้อมแปลสาบานตนสถานทูตตอนนี้</h2>
        <p className="text-blue-100">
          สอบถามขอบเขตงานและขอใบเสนอราคาผ่านโทรศัพท์ · LINE @thainotarylaw · Email
        </p>
      </section>

      <OfficialServiceSources theme="sworn" />
      <RelatedClusters theme="sworn" />

      <p className="mt-6 text-xs text-slate-400">
        เนื้อหาถูกสร้างเฉพาะสำหรับหน้านี้ (page seed) — unique per URL • ~{content.wordCount} คำ
      </p>
      <ContentFreshness path={pathname} locale="th" />
    </article>
  );
}
